'use client';

import { useEffect, useRef } from 'react';
import { logger } from './logger';

/**
 * Hook that requests a screen wake lock to prevent the display from dimming or sleeping.
 * Automatically re-acquires the wake lock when the page becomes visible again.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Screen_Wake_Lock_API
 */
function reportWakeLockFailure(phase: 'initial' | 're-acquisition', err: unknown) {
    // Browsers reject wakeLock.request() without a preceding user gesture
    // (or, for some platforms, outside a PWA standalone window). Test
    // contexts and cold loads will hit this; it's documented browser
    // policy, not a kiosk defect, so don't surface it as an error.
    if (err instanceof DOMException && err.name === 'NotAllowedError') {
        logger.debug(`Wake lock ${phase} request not granted (no user gesture)`);
        return;
    }
    logger.error(`Wake lock ${phase} request failed`, {error: err as Error});
}

export function useWakeLock(): void {
    const wakeLockRef = useRef<WakeLockSentinel | null>(null);

    useEffect(() => {
        // Check if Wake Lock API is supported
        if (!('wakeLock' in navigator)) {
            logger.debug('Wake Lock API not supported');
            return;
        }

        async function requestWakeLock(): Promise<void> {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
            logger.debug('Wake lock acquired');
        }

        function handleVisibilityChange(): void {
            // Only request if page is visible and we don't have an active lock
            if (document.visibilityState === 'visible' && (!wakeLockRef.current || wakeLockRef.current.released)) {
                requestWakeLock().catch((err: unknown) => reportWakeLockFailure('re-acquisition', err));
            }
        }

        // Request wake lock on mount
        requestWakeLock().catch((err: unknown) => reportWakeLockFailure('initial', err));

        // Re-acquire wake lock when page becomes visible
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (wakeLockRef.current) {
                wakeLockRef.current.release();
                wakeLockRef.current = null;
            }
        };
    }, []);
}
